import {useState, useEffect} from "react";
import {useQuery} from "@tanstack/react-query";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@sakura-grp/ui/drawer";
import {
  Command,
  CommandInput,
  CommandList,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@sakura-grp/ui/command";
import {searchCompanies, type CompanySuggestion} from "@/lib/api/business-cards";
import {useKeyboardInset} from "@/hooks/useKeyboardInset";
import {shouldOfferCreate} from "./company-select-create";

const EMPTY: CompanySuggestion[] = [];

interface CompanySelectSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 現在選択中の会社名（一覧でのハイライト用） */
  value?: string;
  /** 会社を選んだ / 新しい会社名を登録したとき。会社名を返す */
  onSelect: (companyName: string) => void;
}

/**
 * 会社の単一フロー Creatable Combobox（cmdk Command）。
 * 1つの検索入力で「探す」、候補末尾の「＋ 新しい会社として登録」行で「作る」を
 * インラインに統合（モード往復なし）。選択も作成も会社名文字列を返し、
 * サーバ側 normalize_company_name が既存一致なら重複させずに解決する。
 * admin の customer-select / hostess-select と同じ cmdk パターンに準拠。
 * 登録・編集の両方から再利用できる共有部品。
 */
export function CompanySelectSheet({open, onOpenChange, value, onSelect}: CompanySelectSheetProps) {
  const [searchQuery, setSearchQuery] = useState(""); // 入力の即時値（表示用）
  const [debouncedQuery, setDebouncedQuery] = useState(""); // RPC 用の確定値
  const [isComposing, setIsComposing] = useState(false); // IME 変換中
  const keyboardInset = useKeyboardInset();

  // 開くたびにリセット
  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setDebouncedQuery("");
      setIsComposing(false);
    }
  }, [open]);

  // debounce（admin パターン準拠）: IME 変換中は確定させない
  useEffect(() => {
    if (isComposing) return;
    if (searchQuery === debouncedQuery) return;
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, debouncedQuery, isComposing]);

  // 会社検索（RPC は 2 文字未満で空を返す）
  const q = debouncedQuery.trim();
  const {data: companies = EMPTY, isFetching} = useQuery({
    queryKey: ["company-select", q],
    queryFn: () => searchCompanies(q),
    enabled: open && q.length >= 2,
    staleTime: 60_000,
  });

  const pick = (companyName: string) => {
    onSelect(companyName.trim());
    onOpenChange(false);
  };

  // 作成行はロード完了後のみ（ロード前に「無い」と誤判定させない）
  const showCreate = !isFetching && shouldOfferCreate(debouncedQuery, companies);
  const createName = q;

  return (
    <Drawer open={open} onOpenChange={onOpenChange} repositionInputs={false}>
      <DrawerContent
        className="px-lg pb-safe-min flex h-[85vh] flex-col"
        style={keyboardInset > 0 ? {paddingBottom: keyboardInset} : undefined}
      >
        <DrawerHeader className="pb-md">
          <DrawerTitle>会社を選ぶ</DrawerTitle>
          <DrawerDescription>
            会社名を入力して選ぶか、見つからなければそのまま登録できます（似た会社があれば既存にまとまります）
          </DrawerDescription>
        </DrawerHeader>

        <Command shouldFilter={false} className="min-h-0 flex-1 rounded-md border">
          <CommandInput
            placeholder="会社名を入力"
            value={searchQuery}
            onValueChange={setSearchQuery}
            onCompositionStart={() => setIsComposing(true)}
            onCompositionEnd={() => setIsComposing(false)}
          />
          <CommandList className="max-h-none flex-1">
            {q.length < 2 ? (
              <p className="text-muted-foreground py-6 text-center text-sm">
                {searchQuery.trim().length === 0
                  ? "会社名を入力すると候補が出ます"
                  : "あと1文字入力すると候補が出ます"}
              </p>
            ) : isFetching && companies.length === 0 ? (
              <p className="text-muted-foreground py-6 text-center text-sm">探しています…</p>
            ) : (
              <>
                {companies.length > 0 && (
                  <CommandGroup heading="会社">
                    {companies.map((c) => (
                      <CommandItem
                        key={c.id}
                        value={c.id}
                        onSelect={() => pick(c.name)}
                        className="gap-sm text-base"
                      >
                        <span className="flex-1">{c.name}</span>
                        {c.name === value && <span className="text-brand">✓</span>}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {showCreate && (
                  <>
                    {companies.length > 0 && <CommandSeparator />}
                    <CommandGroup>
                      <CommandItem
                        value="__create_new_company__"
                        onSelect={() => pick(createName)}
                        className="gap-sm text-base"
                      >
                        <span className="text-brand shrink-0">＋</span>
                        <span>
                          「{createName}」を<span className="font-bold">新しい会社</span>として登録
                        </span>
                      </CommandItem>
                    </CommandGroup>
                  </>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </DrawerContent>
    </Drawer>
  );
}
